import { Component } from '@theme/component';

/**
 * Arc slider component with seamless infinite loop using cloned slides.
 * @extends {Component}
 */
class ArcSlider extends Component {
  #currentIndex = 0;
  #slideElements = [];
  #totalOriginalSlides = 0;
  #autoplayInterval = null;
  #isTransitioning = false;
  #userPaused = false;
  #slideRotation = 10;
  #slideGap = 110;

  // Touch/drag state
  #isDragging = false;
  #startX = 0;
  #startY = 0;
  #currentX = 0;
  #dragOffset = 0;
  #lastMoveTime = 0;
  #velocity = 0;
  #velocityThreshold = 0.3;
  #dragThreshold = 30;
  #containerWidth = 0;
  #touchDirection = null; // 'horizontal' | 'vertical' | null

  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => this.#initializeSlider());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopAutoplay();
    window.removeEventListener('resize', this.#onResize);
  }

  #onResize = () => {
    this.#containerWidth = this.offsetWidth;
    this.#positionAllSlides(true);
  };

  #initializeSlider() {
    const slidesContainer = this.refs.slides || this.querySelector('.c-arc-slider__slides');
    const originalSlides = Array.from(slidesContainer?.querySelectorAll('c-arc-slide') || []);

    if (originalSlides.length === 0) return;

    this.#totalOriginalSlides = originalSlides.length;
    this.#containerWidth = this.offsetWidth;

    // For infinite loop, clone slides at both ends
    if (this.hasAttribute('infinite') && originalSlides.length >= 3) {
      this.#setupInfiniteClones(slidesContainer, originalSlides);
    }

    // Get all slides including clones
    this.#slideElements = Array.from(slidesContainer?.querySelectorAll('c-arc-slide') || []);

    // Start at first real slide (after prepended clones)
    const hasClones = this.hasAttribute('infinite') && originalSlides.length >= 3;
    const clonesAtStart = hasClones ? Math.min(2, originalSlides.length) : 0;
    this.#currentIndex = clonesAtStart;
    this.#positionAllSlides(true);
    this.#createPagination();

    /*
      a11y: the carousel rotates itself and each change slides and rotates the
      cards, so it starts PAUSED for a visitor who asked the OS to minimise
      animation. It is not disabled outright — the play/pause control is left in
      its "paused" state, so the same visitor can still start the rotation if they
      want it. The slide transitions themselves are dropped in CSS.
    */
    if (this.#prefersReducedMotion()) {
      this.#userPaused = true;
      this.setAttribute('paused', '');
      this.#updateAutoplayButton();
    }

    this.#startAutoplay();

    this.addEventListener('mouseenter', () => this.#stopAutoplay());
    this.addEventListener('mouseleave', () => this.#startAutoplay());

    // Pause rotation while any control/slide inside the carousel has keyboard focus,
    // and resume once focus leaves the carousel entirely (unless the user paused it).
    this.addEventListener('focusin', () => this.#stopAutoplay());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.#startAutoplay();
    });

    this.#initDragEvents();
    window.addEventListener('resize', this.#onResize);
  }

  #setupInfiniteClones(container, originalSlides) {
    // Clone last 2 slides and prepend
    for (let i = originalSlides.length - 1; i >= Math.max(0, originalSlides.length - 2); i--) {
      const clone = originalSlides[i].cloneNode(true);
      clone.setAttribute('data-clone', 'prepend');
      clone.removeAttribute('id');
      container.insertBefore(clone, container.firstChild);
    }

    // Clone first 2 slides and append
    for (let i = 0; i < Math.min(2, originalSlides.length); i++) {
      const clone = originalSlides[i].cloneNode(true);
      clone.setAttribute('data-clone', 'append');
      clone.removeAttribute('id');
      container.appendChild(clone);
    }
  }

  #initDragEvents() {
    this.addEventListener('mousedown', this.#onDragStart.bind(this));
    document.addEventListener('mousemove', this.#onDragMove.bind(this));
    document.addEventListener('mouseup', this.#onDragEnd.bind(this));

    this.addEventListener('touchstart', this.#onDragStart.bind(this), { passive: true });
    this.addEventListener('touchmove', this.#onDragMove.bind(this), { passive: false });
    this.addEventListener('touchend', this.#onDragEnd.bind(this));
  }

  #onDragStart(event) {
    if (this.#isTransitioning) return;

    this.#isDragging = true;
    this.#touchDirection = null;
    this.#startX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX;
    this.#startY = event.type === 'touchstart' ? event.touches[0].clientY : event.clientY;
    this.#currentX = this.#startX;
    this.#dragOffset = 0;
    this.#lastMoveTime = Date.now();
    this.#velocity = 0;

    this.#setTransitionsEnabled(false);
    this.#stopAutoplay();
  }

  #onDragMove(event) {
    if (!this.#isDragging) return;

    const isTouch = event.type === 'touchmove';
    const newX = isTouch ? event.touches[0].clientX : event.clientX;
    const newY = isTouch ? event.touches[0].clientY : event.clientY;

    if (isTouch && this.#touchDirection === null) {
      const dx = Math.abs(newX - this.#startX);
      const dy = Math.abs(newY - this.#startY);
      if (dx > 5 || dy > 5) {
        this.#touchDirection = dx > dy ? 'horizontal' : 'vertical';
      }
    }

    if (isTouch && this.#touchDirection === 'vertical') {
      this.#isDragging = false;
      this.#setTransitionsEnabled(true);
      this.#dragOffset = 0;
      this.#positionAllSlides();
      this.#startAutoplay();
      return;
    }

    if (isTouch && this.#touchDirection === 'horizontal' && event.cancelable) {
      event.preventDefault();
    }

    const now = Date.now();
    const dt = now - this.#lastMoveTime;

    if (dt > 0) {
      this.#velocity = (newX - this.#currentX) / dt;
    }

    this.#currentX = newX;
    this.#lastMoveTime = now;
    this.#dragOffset = this.#currentX - this.#startX;

    this.#updateSlidesWithDrag();
  }

  #onDragEnd() {
    if (!this.#isDragging) return;

    this.#isDragging = false;
    this.#setTransitionsEnabled(true);

    const absVelocity = Math.abs(this.#velocity);
    const absDelta = Math.abs(this.#dragOffset);

    const isQuickSwipe = absVelocity > this.#velocityThreshold;
    const exceedsThreshold = absDelta > this.#dragThreshold;

    if (isQuickSwipe || exceedsThreshold) {
      const direction = isQuickSwipe ? this.#velocity : this.#dragOffset;
      if (direction > 0) {
        this.previous();
      } else {
        this.next();
      }
    } else {
      this.#dragOffset = 0;
      this.#positionAllSlides();
    }

    this.#startAutoplay();
  }

  #setTransitionsEnabled(enabled) {
    for (const slide of this.#slideElements) {
      if (enabled) {
        slide.style.transition = '';
        slide.style.willChange = '';
      } else {
        slide.style.transition = 'none';
        slide.style.willChange = 'transform';
      }
    }
  }

  #updateSlidesWithDrag() {
    const isMobile = this.#isMobile();
    const slideWidth = isMobile ? this.#containerWidth : (this.#slideElements[0]?.offsetWidth || 400);
    const gap = isMobile ? 20 : this.#slideGap;

    for (const [index, slide] of this.#slideElements.entries()) {
      const offset = index - this.#currentIndex;

      if (isMobile) {
        const mobileSlideWidth = this.#slideElements[0]?.offsetWidth || this.#containerWidth;
        const baseX = offset * (mobileSlideWidth + gap);
        const x = baseX + this.#dragOffset;
        slide.style.transform = `translateX(${x}px)`;
        slide.style.zIndex = offset === 0 ? '10' : '1';
      } else {
        const baseX = offset * (slideWidth + gap);
        const x = baseX + this.#dragOffset;
        const y = offset === 0 ? 0 : 15;
        const rotation = offset * this.#slideRotation;

        slide.style.transform = `translateX(calc(-50% + ${x}px)) translateY(${y}%) rotate(${rotation}deg)`;
      }
    }
  }

  #isMobile() {
    return window.innerWidth < 750;
  }

  /**
   * Toggles a slide's exposure to assistive tech and keyboard users.
   * The active slide is exposed; inactive slides are hidden and their focusable
   * descendants are pulled out of the tab order (original tabindex is preserved).
   * @param {Element} slide
   * @param {boolean} isActive
   */
  #setSlideInteractivity(slide, isActive) {
    if (isActive) {
      slide.removeAttribute('aria-hidden');
    } else {
      slide.setAttribute('aria-hidden', 'true');
    }

    const focusables = slide.querySelectorAll(
      'a[href], button, input, select, textarea, iframe, [tabindex], [contenteditable="true"], audio[controls], video[controls]'
    );

    for (const element of focusables) {
      if (isActive) {
        const previous = element.dataset.arcPrevTabindex;
        if (previous === undefined) continue;
        if (previous === '') {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', previous);
        }
        delete element.dataset.arcPrevTabindex;
      } else {
        if (element.dataset.arcPrevTabindex === undefined) {
          element.dataset.arcPrevTabindex = element.getAttribute('tabindex') ?? '';
        }
        element.setAttribute('tabindex', '-1');
      }
    }
  }

  #positionAllSlides(immediate = false) {
    const isMobile = this.#isMobile();
    this.#dragOffset = 0;

    for (const [index, slide] of this.#slideElements.entries()) {
      // Simple linear offset from current index
      const offset = index - this.#currentIndex;
      const absOffset = Math.abs(offset);
      const slideWidth = slide.offsetWidth || 400;

      // Update classes
      slide.classList.remove('arc-slide-before', 'arc-slide-current', 'arc-slide-after');
      if (offset < 0) {
        slide.classList.add('arc-slide-before');
      } else if (offset === 0) {
        slide.classList.add('arc-slide-current');
      } else {
        slide.classList.add('arc-slide-after');
      }

      if (immediate) {
        slide.style.transition = 'none';
      }

      if (isMobile) {
        const gap = 20;
        const mobileSlideWidth = this.#slideElements[0]?.offsetWidth || this.#containerWidth;
        const x = offset * (mobileSlideWidth + gap);
        slide.style.transform = `translateX(${x}px)`;
        slide.style.zIndex = offset === 0 ? '10' : '1';
        slide.style.opacity = '1';
        slide.style.visibility = 'visible';
      } else {
        const x = offset * (slideWidth + this.#slideGap);
        const y = offset === 0 ? 0 : 15;
        const rotation = offset * this.#slideRotation;

        slide.style.transform = `translateX(calc(-50% + ${x}px)) translateY(${y}%) rotate(${rotation}deg)`;
        slide.style.zIndex = String(10 - absOffset);
        slide.style.boxShadow = offset === 0 ? '0 10px 40px rgb(0 0 0 / 0.2)' : 'none';

        // Show slides within range, hide others
        const isVisible = absOffset <= 2;
        slide.style.opacity = isVisible ? '1' : '0';
        slide.style.visibility = isVisible ? 'visible' : 'hidden';
      }

      slide.setAttribute('data-offset', String(offset));

      if (offset === 0) {
        slide.setAttribute('data-active', '');
      } else {
        slide.removeAttribute('data-active');
      }

      // Expose only the current slide to screen readers and keyboard navigation;
      // hide all others (side peeks and infinite-loop clones) from the a11y tree
      // and remove their focusable descendants from the tab order.
      this.#setSlideInteractivity(slide, offset === 0);

      if (immediate) {
        void slide.offsetHeight;
        requestAnimationFrame(() => {
          slide.style.transition = '';
        });
      }
    }

    this.#updateNavigationState();
    this.#updatePagination();
  }

  #jumpToIndex(newIndex, animate = false) {
    if (!animate) {
      // Instant jump without animation
      for (const slide of this.#slideElements) {
        slide.style.transition = 'none';
      }
    }

    this.#currentIndex = newIndex;
    this.#positionAllSlides(!animate);

    if (!animate) {
      void document.body.offsetHeight;
      requestAnimationFrame(() => {
        for (const slide of this.#slideElements) {
          slide.style.transition = '';
        }
      });
    }
  }

  #updateNavigationState() {
    const { prevButton, nextButton } = this.refs;
    // Infinite mode: never disable
    if (prevButton) prevButton.disabled = false;
    if (nextButton) nextButton.disabled = false;
  }

  #createPagination() {
    const pagination = this.refs.pagination || this.querySelector('.c-arc-slider__pagination');
    if (!pagination) return;

    pagination.innerHTML = '';
    // Only create dots for original slides, not clones
    for (let i = 0; i < this.#totalOriginalSlides; i++) {
      // Each dot is a list item so the pagination reads as a list (a11y).
      const item = document.createElement('li');
      item.className = 'c-arc-slider__dot-item';

      const dot = document.createElement('button');
      dot.className = 'c-arc-slider__dot';
      dot.type = 'button';
      // The dot has no text of its own, so its accessible name comes entirely
      // from here. Guarded so a missing template leaves the attribute off rather
      // than setting an empty one, which would name the button "".
      const label = this.#slideStatusText(i);
      if (label) dot.setAttribute('aria-label', label);
      if (this.#getRealIndex() === i) {
        dot.setAttribute('aria-current', 'true');
      }
      dot.addEventListener('click', () => this.goToSlide(i));

      item.appendChild(dot);
      pagination.appendChild(item);
    }
  }

  #updatePagination() {
    const pagination = this.refs.pagination || this.querySelector('.c-arc-slider__pagination');
    if (!pagination) return;

    const realIndex = this.#getRealIndex();
    const dots = pagination.querySelectorAll('.c-arc-slider__dot');
    for (const [index, dot] of Array.from(dots).entries()) {
      if (index === realIndex) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Builds the translated "Slide 2 of 5" text for a slide.
   *
   * The dots and the live region are created in JS, which cannot call Liquid's
   * `t` filter, so the block hands the translated string over on the host element
   * with placeholders for the two numbers (a11y / localization).
   * @param {number} realIndex Zero-based index among the original (non-clone) slides.
   * @returns {string} Empty when the block did not provide the template.
   */
  #slideStatusText(realIndex) {
    const template = this.dataset.slideStatus;
    if (!template) return '';

    return template.replace('__index__', String(realIndex + 1)).replace('__length__', String(this.#totalOriginalSlides));
  }

  /**
   * Announces the slide the visitor just moved to.
   *
   * Only for changes the visitor made themselves — autoplay deliberately stays
   * silent, since an announcement every few seconds would interrupt whatever is
   * being read. Skipped while the carousel is still initialising, so entering the
   * region does not immediately announce slide 1 on top of the instructions.
   */
  #announceSlide() {
    const region = this.refs.liveRegion;
    if (!region) return;

    const text = this.#slideStatusText(this.#getRealIndex());
    if (text) region.textContent = text;
  }

  /**
   * Whether the visitor asked the operating system to minimise animation.
   * @returns {boolean}
   */
  #prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  #getRealIndex() {
    // With clones: first 2 slides are clones of last 2
    // Real slides start at index 2
    const hasClones = this.hasAttribute('infinite') && this.#totalOriginalSlides >= 3;
    if (!hasClones) return this.#currentIndex;

    const clonesAtStart = Math.min(2, this.#totalOriginalSlides);
    let realIndex = this.#currentIndex - clonesAtStart;

    // Normalize to 0..totalOriginalSlides-1
    while (realIndex < 0) realIndex += this.#totalOriginalSlides;
    while (realIndex >= this.#totalOriginalSlides) realIndex -= this.#totalOriginalSlides;

    return realIndex;
  }

  #startAutoplay() {
    this.#stopAutoplay();

    // Respect an explicit user pause: hover/focus should not resume rotation.
    if (this.#userPaused) return;

    const autoplayAttr = this.getAttribute('autoplay');
    if (!autoplayAttr) return;

    const interval = parseInt(autoplayAttr, 10) * 1000;
    if (isNaN(interval) || interval <= 0) return;

    this.#autoplayInterval = setInterval(() => {
      if (!this.#isTransitioning && !this.#isDragging) {
        // announce: false — see #announceSlide.
        this.next(null, false);
      }
    }, interval);
  }

  #stopAutoplay() {
    if (this.#autoplayInterval) {
      clearInterval(this.#autoplayInterval);
      this.#autoplayInterval = null;
    }
  }

  /**
   * Toggles automatic rotation in response to the play/pause control.
   * @param {Event} [event]
   */
  toggleAutoplay(event) {
    event?.preventDefault();
    if (this.#userPaused) {
      this.#userPaused = false;
      this.removeAttribute('paused');
      this.#startAutoplay();
    } else {
      this.#userPaused = true;
      this.setAttribute('paused', '');
      this.#stopAutoplay();
    }
    this.#updateAutoplayButton();
  }

  #updateAutoplayButton() {
    const button = this.refs.autoplayToggle;
    if (!button) return;
    const label = this.#userPaused ? button.dataset.labelPlay : button.dataset.labelPause;
    if (label) button.setAttribute('aria-label', label);
  }

  /**
   * @param {Event} [event]
   * @param {boolean} [announce] Whether to announce the new slide; false for autoplay.
   */
  next(event, announce = true) {
    event?.preventDefault();
    if (this.#isTransitioning) return;

    const hasClones = this.hasAttribute('infinite') && this.#totalOriginalSlides >= 3;
    const clonesAtStart = hasClones ? Math.min(2, this.#totalOriginalSlides) : 0;
    const clonesAtEnd = hasClones ? Math.min(2, this.#totalOriginalSlides) : 0;
    const maxIndex = this.#slideElements.length - 1 - clonesAtEnd;

    let nextIndex = this.#currentIndex + 1;

    this.#isTransitioning = true;
    this.#currentIndex = nextIndex;
    this.#positionAllSlides();
    // After positioning, so #getRealIndex() reports the slide that is now shown
    // (including the wrap back to the first slide at the clone boundary).
    if (announce) this.#announceSlide();

    const transitionDuration = this.#isMobile() ? 250 : 400;

    // Check if we need to jump back after animation
    if (hasClones && nextIndex > maxIndex) {
      setTimeout(() => {
        // Jump to the real first slide (after prepended clones)
        this.#jumpToIndex(clonesAtStart, false);
        this.#isTransitioning = false;
      }, transitionDuration);
    } else {
      setTimeout(() => {
        this.#isTransitioning = false;
      }, transitionDuration);
    }
  }

  /**
   * @param {Event} [event]
   * @param {boolean} [announce] Whether to announce the new slide.
   */
  previous(event, announce = true) {
    event?.preventDefault();
    if (this.#isTransitioning) return;

    const hasClones = this.hasAttribute('infinite') && this.#totalOriginalSlides >= 3;
    const clonesAtStart = hasClones ? Math.min(2, this.#totalOriginalSlides) : 0;
    const minIndex = clonesAtStart;

    let prevIndex = this.#currentIndex - 1;

    this.#isTransitioning = true;
    this.#currentIndex = prevIndex;
    this.#positionAllSlides();
    if (announce) this.#announceSlide();

    const transitionDuration = this.#isMobile() ? 250 : 400;

    // Check if we need to jump forward after animation
    if (hasClones && prevIndex < minIndex) {
      setTimeout(() => {
        // Jump to the real last slide
        const lastRealIndex = clonesAtStart + this.#totalOriginalSlides - 1;
        this.#jumpToIndex(lastRealIndex, false);
        this.#isTransitioning = false;
      }, transitionDuration);
    } else {
      setTimeout(() => {
        this.#isTransitioning = false;
      }, transitionDuration);
    }
  }

  goToSlide(realIndex, event) {
    event?.preventDefault();
    if (this.#isTransitioning) return;

    const hasClones = this.hasAttribute('infinite') && this.#totalOriginalSlides >= 3;
    const clonesAtStart = hasClones ? Math.min(2, this.#totalOriginalSlides) : 0;

    // Convert real index to internal index
    const targetIndex = realIndex + clonesAtStart;

    if (targetIndex !== this.#currentIndex) {
      this.#isTransitioning = true;
      this.#currentIndex = targetIndex;
      this.#positionAllSlides();
      this.#announceSlide();

      const transitionDuration = this.#isMobile() ? 250 : 400;
      setTimeout(() => {
        this.#isTransitioning = false;
      }, transitionDuration);
    }
  }
}

if (!customElements.get('c-arc-slider')) {
  customElements.define('c-arc-slider', ArcSlider);
}
